import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  getProductById,
  computeDiscountedPrice,
  getProductDiscountPct,
  variantKey,
} from "@/data/products";
import { useStock } from "@/context/StockContext";

// v3 schema introduces color-keyed lines on top of v2's size keys.
const STORAGE_KEY = "bob_cart_v3";
const LEGACY_KEY_V2 = "bob_cart_v2";

const CartContext = createContext(null);

const lineKey = (id, size, color) =>
  `${id}::${size || ""}::${color || ""}`;

/**
 * Resolve the live remaining stock for a (productId, size, color) line.
 * Falls back to the product's hardcoded seed quantity while the stock map is
 * still loading so the (+) button can never run unbounded.
 *
 * Returns a non-negative integer.
 */
const resolveStock = (stockMap, productId, size, color) => {
  const product = getProductById(productId);
  const entry = stockMap?.[productId];

  // Compute the conservative product-data fallback first so it's always
  // available even when the backend stock map hasn't loaded yet.
  let fallback = Infinity;
  if (product) {
    const hasSizes = Array.isArray(product.sizes) && product.sizes.length > 0;
    if (hasSizes) {
      const sz = product.sizes.find((s) => s.label === size);
      if (sz) fallback = sz.qty == null ? 1 : Math.max(0, Number(sz.qty) || 0);
      else fallback = 1;
    } else if (product.quantity != null) {
      fallback = Math.max(0, Number(product.quantity) || 0);
    } else {
      fallback = 1;
    }
  }

  if (!entry) return fallback;

  if (entry.has_sizes) {
    const vk = variantKey(size, color);
    if (!vk) {
      // Should not happen for a sized product, but be safe.
      return fallback;
    }
    const live = (entry.sizes || {})[vk];
    if (live == null) return fallback;
    return Math.max(0, Number(live) || 0);
  }

  // No sizes — single SKU bucket.
  return Math.max(0, Number(entry.qty || 0));
};

export const CartProvider = ({ children }) => {
  const { stockMap, loaded: stockLoaded } = useStock();

  // items: [{ id, size, color, qty }]
  // Only non-sensitive cart structure is persisted. No PII, payments, or
  // customer info ever touches localStorage.
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
      // One-time migration from v2 → v3: copy existing lines with color=""
      const legacy = localStorage.getItem(LEGACY_KEY_V2);
      if (legacy) {
        const parsed = JSON.parse(legacy);
        if (Array.isArray(parsed)) {
          return parsed.map((i) => ({ ...i, color: i.color || "" }));
        }
      }
    } catch (err) {
      console.warn("[Cart] Failed to read cart from localStorage:", err);
    }
    return [];
  });
  const [isOpen, setIsOpen] = useState(false);

  // Number of cart lines clamped down due to live stock. Bumping this counter
  // lets UI surfaces show a toast exactly once per backend sync event.
  const [autoClampSignal, setAutoClampSignal] = useState(0);
  const lastClampSignature = useRef("");

  // ----- Persist to localStorage -----
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn("[Cart] Failed to persist cart to localStorage:", err);
    }
  }, [items]);

  // ----- Stock-aware auto-clamp -----
  // Whenever the live stock map updates (or items change), trim any line whose
  // qty exceeds the freshly-known stock. This is the single source of truth
  // for the "no overselling" invariant and runs:
  //   - on first stock load (clamps restored localStorage entries)
  //   - after every order/inventory change
  //   - after any in-memory mutation that might have raced past the cap
  useEffect(() => {
    if (!stockLoaded) return;
    let didClamp = false;
    setItems((prev) => {
      let changed = false;
      const next = prev
        .map((i) => {
          const max = resolveStock(stockMap, i.id, i.size, i.color);
          if (max <= 0) {
            changed = true;
            return null;
          }
          if (i.qty > max) {
            changed = true;
            didClamp = true;
            return { ...i, qty: max };
          }
          return i;
        })
        .filter(Boolean);
      return changed ? next : prev;
    });
    if (didClamp) {
      // Use a signature so the same clamp event doesn't trigger duplicate
      // toasts when React re-runs effects.
      const sig = `${Date.now()}`;
      if (lastClampSignature.current !== sig) {
        lastClampSignature.current = sig;
        setAutoClampSignal((n) => n + 1);
      }
    }
  }, [stockLoaded, stockMap]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Add `qty` units of (id, size, color) to the cart, never letting the
   * resulting line exceed the live remaining stock. Returns:
   *   - acceptedQty: number actually added (0 means no-op, stock full)
   *   - cappedAt:    the cap that triggered (null when nothing was capped)
   */
  const addToCart = useCallback(
    (id, qty = 1, size = "", color = "") => {
      const max = resolveStock(stockMap, id, size, color);
      let acceptedQty = 0;
      let cappedAt = null;
      setItems((prev) => {
        const idx = prev.findIndex(
          (i) => lineKey(i.id, i.size, i.color) === lineKey(id, size, color)
        );
        const currentQty = idx >= 0 ? prev[idx].qty : 0;
        let nextQty = currentQty + qty;
        if (nextQty > max) {
          nextQty = Math.max(currentQty, max);
          cappedAt = max;
        }
        acceptedQty = nextQty - currentQty;
        if (idx >= 0) {
          if (nextQty === currentQty) return prev; // nothing actually added
          const next = [...prev];
          next[idx] = { ...next[idx], qty: nextQty };
          return next;
        }
        if (nextQty <= 0) return prev;
        return [...prev, { id, size, color, qty: nextQty }];
      });
      return { acceptedQty, cappedAt };
    },
    [stockMap]
  );

  /**
   * Set the exact quantity of an existing cart line, clamped to live stock.
   * Removing the line entirely when the requested qty reaches 0.
   */
  const updateQty = useCallback(
    (key, qty) => {
      let cappedAt = null;
      setItems((prev) =>
        prev
          .map((i) => {
            if (lineKey(i.id, i.size, i.color) !== key) return i;
            const max = resolveStock(stockMap, i.id, i.size, i.color);
            let q = qty;
            if (q > max) {
              q = max;
              cappedAt = max;
            }
            return { ...i, qty: q };
          })
          .filter((i) => i.qty > 0)
      );
      return { cappedAt };
    },
    [stockMap]
  );

  const removeFromCart = useCallback((key) => {
    setItems((prev) =>
      prev.filter((i) => lineKey(i.id, i.size, i.color) !== key)
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  // Current cart qty for a specific (id, size, color) — used by callers to
  // compute "how many more can I add?".
  const lineQty = useCallback(
    (id, size = "", color = "") => {
      const found = items.find(
        (i) => lineKey(i.id, i.size, i.color) === lineKey(id, size, color)
      );
      return found ? found.qty : 0;
    },
    [items]
  );

  // Remaining cap for a variant = max stock - current cart qty.
  const variantRemaining = useCallback(
    (id, size = "", color = "") => {
      const max = resolveStock(stockMap, id, size, color);
      const inCart = lineQty(id, size, color);
      return Math.max(0, max - inCart);
    },
    [stockMap, lineQty]
  );

  // Live stock for a variant (independent of what's already in the cart).
  const variantStockOf = useCallback(
    (id, size = "", color = "") => resolveStock(stockMap, id, size, color),
    [stockMap]
  );

  // Enriched items with product data + line totals
  const detailedItems = useMemo(
    () =>
      items
        .map((i) => {
          const product = getProductById(i.id);
          if (!product) return null;
          const unitPrice = computeDiscountedPrice(
            product.originalPrice,
            getProductDiscountPct(product)
          );
          if (unitPrice == null) return null; // price-TBD — skip from totals
          return {
            key: lineKey(i.id, i.size, i.color),
            id: i.id,
            size: i.size || "",
            color: i.color || "",
            qty: i.qty,
            product,
            unitPrice,
            lineTotal: Math.round(unitPrice * i.qty * 100) / 100,
            maxQty: resolveStock(stockMap, i.id, i.size, i.color),
          };
        })
        .filter(Boolean),
    [items, stockMap]
  );

  const totalItems = useMemo(
    () => items.reduce((acc, i) => acc + i.qty, 0),
    [items]
  );

  const subtotal = useMemo(
    () =>
      Math.round(
        detailedItems.reduce((acc, i) => acc + i.lineTotal, 0) * 100
      ) / 100,
    [detailedItems]
  );

  // Flat shipping fee for all Lebanese territories.
  const SHIPPING_FEE = 6;
  const shippingFee = detailedItems.length > 0 ? SHIPPING_FEE : 0;
  const total = Math.round((subtotal + shippingFee) * 100) / 100;

  const value = useMemo(
    () => ({
      items,
      detailedItems,
      totalItems,
      subtotal,
      shippingFee,
      total,
      isOpen,
      addToCart,
      updateQty,
      removeFromCart,
      clearCart,
      openCart,
      closeCart,
      lineQty,
      variantStockOf,
      variantRemaining,
      autoClampSignal,
    }),
    [items, detailedItems, totalItems, subtotal, shippingFee, total, isOpen, addToCart, updateQty, removeFromCart, clearCart, openCart, closeCart, lineQty, variantStockOf, variantRemaining, autoClampSignal]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

export { lineKey };
